/**
 * Parcel List Component (Manager View)
 * ====================================
 *
 * Liste complète des colis pour les managers
 * Basé sur history.component mais avec fonctionnalités CRUD et filtres avancés
 *
 * Différences avec History (client view):
 * - Affiche TOUS les colis (pas seulement userId)
 * - Sélection multiple + bulk actions
 * - Filtres par country, status, type
 * - Actions CRUD (edit, delete)
 *
 * @version 2.0.0 - Refactoring Befret
 * @author BeFret Development Team
 */

import { Component, OnInit, OnDestroy, ViewChild, AfterViewInit } from '@angular/core';
import { Firestore, collection, collectionData, query, orderBy } from '@angular/fire/firestore';
import { getDisplayPrice } from '../core/utils/display-price';
import { AuthService } from '../core/services/auth.service';
import { ActivatedRoute, Router } from '@angular/router';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { SelectionModel } from '@angular/cdk/collections';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ProductionTranslationService } from '../core/services/production-translation.service';
import { StatusMappingService } from '../core/services/status-mapping.service';

export interface Shipment {
  trackingID: string;
  type: string;
  status: string;
  sender_name: string;
  receiver_name: string;
  city: string;
  cost: number;
  weight: number;
  create_date: Date;
  phonePrefix?: string;
  standardData?: any;
  customerInfo?: any;
  parcelInfo?: any;
  pricing?: any;
  archived?: boolean;
}

import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SharedModule } from '../shared/shared.module';

import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTableModule } from '@angular/material/table';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSortModule } from '@angular/material/sort';
import { MatMenuModule } from '@angular/material/menu';
import { MatChipsModule } from '@angular/material/chips';
import { MatDividerModule } from '@angular/material/divider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';

@Component({
    selector: 'app-parcel-list',
    templateUrl: './parcel-list.component.html',
    styleUrls: ['./parcel-list.component.css'],
    standalone: true,
    imports: [CommonModule, FormsModule, SharedModule, MatIconModule, MatProgressSpinnerModule, MatCardModule, MatCheckboxModule, MatTableModule, MatPaginatorModule, MatSortModule, MatMenuModule, MatChipsModule, MatDividerModule, MatTooltipModule, MatButtonModule]
})
export class ParcelListComponent implements OnInit, AfterViewInit, OnDestroy {
  // Colonnes pour managers - WITH select column + actions
  displayedColumns: string[] = [
    'select',
    'icon',
    'trackingID',
    'sender_name',
    'receiver_name',
    'city',
    'status',
    'cost',
    'weight',
    'create_date',
    'actions'
  ];

  dataSource: MatTableDataSource<Shipment>;
  selection = new SelectionModel<Shipment>(true, []); // Multi-selection for managers

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  userId?: string;
  isLoading = true;
  totalShipments = 0;

  // Query params filters (from dashboard)
  country: string | null = null;
  delivered: string | null = null;
  arrived: string | null = null;
  pending: string | null = null;
  received: string | null = null;
  sent: string | null = null;
  paquet: string | null = null;
  mail: string | null = null;
  statusParam: string | null = null;

  // UI Filters
  filterStatus: string = 'all';
  filterCity: string = 'all';
  filterType: string = 'all';
  filterCountry: string = 'all';
  showArchived: boolean = false;
  activeStatCard: string = 'all';

  // Mobile detection
  isMobile = false;
  showMobileFilters = false;
  private destroy$ = new Subject<void>();

  statusOptions: { value: string; labelKey: string; }[] = [];
  cityOptions: { value: string; labelKey: string; }[] = [];
  typeOptions: { value: string; labelKey: string; }[] = [];
  countryOptions: { value: string; labelKey: string; }[] = [];

  constructor(
    private firestore: Firestore,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private translate: ProductionTranslationService,
    private statusMapper: StatusMappingService,
    private breakpointObserver: BreakpointObserver
  ) {
    this.dataSource = new MatTableDataSource<Shipment>([]);
  }

  ngOnInit(): void {
    // Mobile detection
    this.breakpointObserver.observe(['(max-width: 768px)'])
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        this.isMobile = result.matches;
        if (this.paginator) {
          this.paginator.pageSize = this.isMobile ? 10 : 25;
          this.paginator.firstPage();
        }
      });

    // Initialize translation options
    this.initializeTranslations();

    // Get query params from dashboard
    this.route.queryParams.subscribe(params => {
      this.country = params['country'] || null;
      this.delivered = params['delivered'] || null;
      this.arrived = params['arrived'] || null;
      this.pending = params['pending'] || null;
      this.received = params['received'] || null;
      this.sent = params['sent'] || null;
      this.paquet = params['paquet'] || null;
      this.mail = params['mail'] || null;
      this.statusParam = params['status'] || null;

      console.log('📊 [PARCEL-LIST] Query params:', params);

      // Apply initial filter based on query params
      this.applyQueryParamsFilter();
    });

    // Check user permissions (managers only)
    this.authService.getUser().subscribe(user => {
      if (user) {
        this.userId = user.uid;
        this.fetchAllShipments();
      }
    });
  }

  private initializeTranslations(): void {
    // Status options — aligned with current timeline steps from StatusMappingService
    this.statusOptions = [
      { value: 'all', labelKey: 'HISTORY.STATUS_LIST.ALL' },
      { value: 'order', labelKey: 'HISTORY.STATUS_LIST.ORDER_CONFIRMED' },
      { value: 'collected_europe', labelKey: 'HISTORY.STATUS_LIST.COLLECTED_EUROPE' },
      { value: 'warehouse', labelKey: 'HISTORY.STATUS_LIST.WAREHOUSE_BEFRET' },
      { value: 'befret_transit', labelKey: 'HISTORY.STATUS_LIST.SHIPPED_TO_CONGO' },
      { value: 'congo_distribution', labelKey: 'HISTORY.STATUS_LIST.ARRIVED_CONGO' },
      { value: 'delivered', labelKey: 'HISTORY.STATUS_LIST.DELIVERED' },
      { value: 'quote_pending', labelKey: 'HISTORY.STATUS_LIST.QUOTE_PENDING' },
      { value: 'payment_pending', labelKey: 'HISTORY.STATUS_LIST.PAYMENT_PENDING' },
      // Incident statuses
      { value: 'incident', labelKey: 'HISTORY.STATUS_LIST.INCIDENT' },
      { value: 'lost', labelKey: 'HISTORY.STATUS_LIST.LOST' },
      { value: 'damaged', labelKey: 'HISTORY.STATUS_LIST.DAMAGED' },
      { value: 'poorly_packed', labelKey: 'HISTORY.STATUS_LIST.POORLY_PACKED' },
      { value: 'dangerous_goods', labelKey: 'HISTORY.STATUS_LIST.DANGEROUS_GOODS' },
      { value: 'seized_customs_eu', labelKey: 'HISTORY.STATUS_LIST.SEIZED_CUSTOMS_EU' },
      { value: 'seized_customs_congo', labelKey: 'HISTORY.STATUS_LIST.SEIZED_CUSTOMS_CONGO' }
    ];

    // City options
    this.cityOptions = [
      { value: 'all', labelKey: 'HISTORY.CITY_LIST.ALL' },
      { value: 'Kinshasa', labelKey: 'HISTORY.CITY_LIST.KINSHASA' },
      { value: 'Lubumbashi', labelKey: 'HISTORY.CITY_LIST.LUBUMBASHI' }
    ];

    // Type options
    this.typeOptions = [
      { value: 'all', labelKey: 'HISTORY.TYPE_LIST.ALL' },
      { value: 'mail', labelKey: 'HISTORY.TYPE_LIST.MAIL' },
      { value: 'package', labelKey: 'HISTORY.TYPE_LIST.PACKAGE' },
      { value: 'heavy_parcel', labelKey: 'HISTORY.TYPE_LIST.HEAVY_PARCEL' }
    ];

    // Country options (phone prefixes)
    this.countryOptions = [
      { value: 'all', labelKey: 'PARCEL_LIST.COUNTRY_LIST.ALL' },
      { value: '+243', labelKey: 'PARCEL_LIST.COUNTRY_LIST.CONGO' },
      { value: '+32', labelKey: 'PARCEL_LIST.COUNTRY_LIST.BELGIUM' },
      { value: '+33', labelKey: 'PARCEL_LIST.COUNTRY_LIST.FRANCE' },
      { value: '+31', labelKey: 'PARCEL_LIST.COUNTRY_LIST.NETHERLANDS' }
    ];
  }

  /**
   * Apply filter based on query params from dashboard
   */
  private applyQueryParamsFilter(): void {
    if (this.statusParam) {
      this.filterStatus = this.statusParam;
    } else if (this.pending) {
      this.filterStatus = 'order';
    } else if (this.received) {
      this.filterStatus = 'collected_europe';
    } else if (this.sent) {
      this.filterStatus = 'befret_transit';
    } else if (this.arrived) {
      this.filterStatus = 'congo_distribution';
    } else if (this.delivered) {
      this.filterStatus = 'delivered';
    }

    if (this.paquet) {
      this.filterType = 'package';
    } else if (this.mail) {
      this.filterType = 'mail';
    }

    if (this.country) {
      this.filterCountry = this.country;
    }
  }

  ngAfterViewInit() {
    setTimeout(() => {
      // Set mobile pageSize BEFORE assigning paginator to dataSource
      if (this.isMobile && this.paginator) {
        this.paginator.pageSize = 10;
      }
      this.dataSource.paginator = this.paginator;
      this.dataSource.sort = this.sort;

      // Custom sorting accessor
      this.dataSource.sortingDataAccessor = (item: Shipment, property: string) => {
        switch (property) {
          case 'create_date':
            return item.create_date.getTime();
          case 'cost':
            return item.cost;
          case 'weight':
            return item.weight;
          case 'trackingID':
            return item.trackingID.toLowerCase();
          case 'sender_name':
            return item.sender_name.toLowerCase();
          case 'receiver_name':
            return item.receiver_name.toLowerCase();
          case 'city':
            return item.city.toLowerCase();
          case 'status':
            return item.status.toLowerCase();
          default:
            return (item as any)[property];
        }
      };

      // Custom filter predicate for multiple filters
      this.dataSource.filterPredicate = (data: Shipment, filter: string) => {
        // ✅ FIX: Ignorer le trigger spécial utilisé pour forcer l'exécution des filtres dropdown
        const searchStr = filter === '__TRIGGER__' ? '' : filter.toLowerCase();

        // Archived filter: mode corbeille — ne montrer QUE les supprimés, ou QUE les actifs
        if (this.showArchived && !data.archived) {
          return false;
        }
        if (!this.showArchived && data.archived) {
          return false;
        }

        // Status filter — 'in_transit' and 'incident' are multi-status groups
        if (this.filterStatus !== 'all') {
          if (this.filterStatus === 'in_transit') {
            if (data.status !== 'collected_europe' &&
                data.status !== 'warehouse' &&
                data.status !== 'befret_transit') {
              return false;
            }
          } else if (this.filterStatus === 'incident') {
            const incidentStatuses = ['lost', 'damaged', 'poorly_packed', 'dangerous_goods', 'seized_customs_eu', 'seized_customs_congo'];
            if (!incidentStatuses.includes(data.status)) {
              return false;
            }
          } else if (data.status !== this.filterStatus) {
            return false;
          }
        }

        // City filter - CASE INSENSITIVE
        if (this.filterCity !== 'all') {
          const dataCity = (data.city || '').toLowerCase().trim();
          const filterCity = this.filterCity.toLowerCase().trim();
          if (dataCity !== filterCity) {
            return false;
          }
        }

        // Type filter
        if (this.filterType !== 'all' && data.type !== this.filterType) {
          return false;
        }

        // Country filter (phone prefix)
        if (this.filterCountry !== 'all' && data.phonePrefix !== this.filterCountry) {
          return false;
        }

        // Text search (only if there's actual search text, not the trigger)
        if (searchStr) {
          return (
            data.trackingID?.toLowerCase().includes(searchStr) ||
            data.sender_name?.toLowerCase().includes(searchStr) ||
            data.receiver_name?.toLowerCase().includes(searchStr) ||
            data.city?.toLowerCase().includes(searchStr) ||
            data.status?.toLowerCase().includes(searchStr)
          );
        }

        return true;
      };

      console.log('✅ MatSort and MatPaginator initialized');
    });
  }

  /**
   * Fetch ALL shipments (manager view - not filtered by userId)
   */
  fetchAllShipments() {
    this.isLoading = true;

    console.log('🔍 [PARCEL-LIST] Fetching ALL shipments for manager view');

    const q = query(collection(this.firestore, 'shipments'), orderBy('timestamps.createdAt', 'desc'));
    (collectionData(q) as any)
      .subscribe({
        next: (data: any[]) => {
          console.log('📦 Raw shipments fetched:', data.length);

          const shipments: Shipment[] = data
            .filter((shipment: any) => {
              // Filtre 1: Tracking ID valide
              const trackingID = shipment.trackingNumber;
              if (!trackingID || typeof trackingID !== 'string' || trackingID.trim() === '') {
                return false;
              }

              // Filtre 2: ✅ Paiement complété OU colis lourd (sans paiement immédiat)
              const hasPayment = shipment.paymentFlow?.paymentStatus === 'paid' ||
                                 shipment.paymentFlow?.webhookProcessed === true ||
                                 shipment.standardData?.paymentIntentId ||
                                 shipment.standardData?.dpdIntegration?.shipmentId;

              // ✅ Les colis lourds (category === 'heavy') sont affichés même sans paiement
              const isHeavyParcel = shipment.category === 'heavy';

              return hasPayment || isHeavyParcel;
            })
            .map(shipment => {
              // ✅ Utiliser StatusMappingService pour mapping unifié
              const unifiedStatus = this.statusMapper.mapFirebaseStatusToUnified(
                shipment.status?.current || shipment.status || 'pending',
                shipment.category,
                shipment.webhookStatus
              );

              // Incident statuses: preserve as-is (don't map to 'delivered')
              const incidentStatuses = ['lost', 'damaged', 'poorly_packed', 'dangerous_goods', 'seized_customs_eu', 'seized_customs_congo'];
              const displayStatus = incidentStatuses.includes(unifiedStatus)
                ? unifiedStatus
                : this.statusMapper.mapUnifiedToTimelineStep(unifiedStatus);

              return {
              trackingID: shipment.trackingNumber || 'N/A',
              type: this.mapShipmentType(shipment.parcelInfo?.type ?? shipment.type, shipment.category),
              status: displayStatus,
              sender_name: shipment.customerInfo?.sender?.name ||
                          shipment.customerInfo?.sender?.fullName ||
                          'Expéditeur',
              receiver_name: shipment.customerInfo?.receiver?.name ||
                            shipment.customerInfo?.receiver?.fullName ||
                            'Client',
              city: shipment.customerInfo?.receiver?.address?.city ||
                   shipment.customerInfo?.destination?.city ||
                   'Kinshasa',
              cost: this.extractCost(shipment),
              weight: shipment.parcelInfo?.weight ||
                     shipment.parcelInfo?.dimensions?.weight ||
                     0,
              create_date: this.convertTimestampToDate(shipment.timestamps?.createdAt || shipment.createdAt),
              phonePrefix: shipment.customerInfo?.receiver?.phone?.prefix || '+243',
              standardData: shipment.standardData,
              customerInfo: shipment.customerInfo,
              parcelInfo: shipment.parcelInfo,
              pricing: shipment.pricing,
              archived: shipment.metadata?.flags?.archived === true
            };
            });

          this.dataSource.data = shipments;
          this.totalShipments = shipments.filter(s => !s.archived).length;
          this.isLoading = false;

          // Reapply filters after data load
          if (this.paginator) {
            this.dataSource.paginator = this.paginator;
          }
          if (this.sort) {
            this.dataSource.sort = this.sort;
          }

          // Apply initial query param filters
          this.onFilterChange();

          console.log('✅ Shipments loaded:', shipments.length);
        },
        error: (error: any) => {
          console.error('❌ Error fetching shipments:', error);
          this.isLoading = false;
        }
      });
  }

  /**
   * Map ShipmentType to internal type code
   */
  private mapShipmentType(type: string, category: string): string {
    if (category === 'heavy') {
      return 'heavy_parcel';
    }

    switch (type) {
      case 'standard_shop':
      case 'standard_home':
      case 'befret_home':
        return 'package';
      case 'heavy_specialist':
      case 'heavy_custom':
        return 'heavy_parcel';
      default:
        return 'package';
    }
  }

  /**
   * Extract cost from pricing object
   */
  private extractCost(shipment: any): number {
    // #374 — prix payé remise incluse (garde paymentStatus='paid') avant les
    // fallbacks de devis. standardData.pricing.total est désormais pré-remise.
    const displayPrice = getDisplayPrice(shipment);
    if (displayPrice > 0) return displayPrice;
    if (shipment.pricing?.basePrice) return shipment.pricing.basePrice;
    if (shipment.heavyData?.quotationData?.amount) return shipment.heavyData.quotationData.amount;  // ✅ Correct property
    if (shipment.standardData?.dpdIntegration?.estimatedCost) return shipment.standardData.dpdIntegration.estimatedCost;

    console.warn('⚠️ No pricing found for shipment:', shipment.trackingNumber);
    return 0;
  }

  // === FILTERS ===

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  onFilterChange() {
    const currentFilter = this.dataSource.filter;
    this.dataSource.filter = '';
    setTimeout(() => {
      // ✅ FIX: Utiliser '__TRIGGER__' si pas de recherche textuelle
      // Cela force l'exécution du filterPredicate pour les filtres dropdown
      // Le filterPredicate ignorera ce trigger dans la recherche textuelle
      this.dataSource.filter = currentFilter || '__TRIGGER__';
      if (this.dataSource.paginator) {
        this.dataSource.paginator.firstPage();
      }
    }, 0);
  }

  clearFilters() {
    this.filterStatus = 'all';
    this.filterCity = 'all';
    this.filterType = 'all';
    this.filterCountry = 'all';
    this.showArchived = false;
    this.activeStatCard = 'all';
    this.dataSource.filter = '';
    if (this.isMobile) {
      this.showMobileFilters = false;
    }
  }

  toggleMobileFilters() {
    this.showMobileFilters = !this.showMobileFilters;
  }

  toggleShowArchived() {
    this.showArchived = !this.showArchived;
    this.onFilterChange();
  }

  // === SELECTION ===

  /** Whether the number of selected elements matches the total number of rows. */
  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  toggleAllRows() {
    if (this.isAllSelected()) {
      this.selection.clear();
      return;
    }

    this.selection.select(...this.dataSource.data);
  }

  /** The label for the checkbox on the passed row */
  checkboxLabel(row?: Shipment): string {
    if (!row) {
      return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${row.trackingID}`;
  }

  // === ACTIONS ===

  /**
   * Handle row click — ignore if the click originated from the action button/menu
   */
  onRowClick(event: MouseEvent, trackingID: string): void {
    const target = event.target as HTMLElement;
    if (target.closest('.action-button') || target.closest('.mat-mdc-menu-trigger') || target.closest('mat-menu')) {
      return;
    }
    this.viewDetails(trackingID);
  }

  viewDetails(trackingID: string): void {
    // Navigate to manager order details with all metadata
    this.router.navigate(['/manager-order-details'], { queryParams: { trackingID } });
  }

  editShipment(trackingID: string): void {
    // Navigate to manager order details in edit mode
    this.router.navigate(['/manager-order-details'], {
      queryParams: { trackingID, edit: 'true' }
    });
  }

  deleteShipment(trackingID: string): void {
    if (confirm('Êtes-vous sûr de vouloir supprimer cet envoi?')) {
      // Delete from Firestore (to be implemented)
      console.log('Delete shipment:', trackingID);
    }
  }

  bulkDelete(): void {
    const selected = this.selection.selected;
    if (selected.length === 0) return;

    if (confirm(`Supprimer ${selected.length} envois sélectionnés?`)) {
      console.log('Bulk delete:', selected.map(s => s.trackingID));
      // Implement bulk delete
      this.selection.clear();
    }
  }

  bulkExport(): void {
    const selected = this.selection.selected;
    if (selected.length === 0) {
      alert('Aucun envoi sélectionné');
      return;
    }

    const csv = this.convertToCSV(selected);
    this.downloadCSV(csv, 'envois-selectionnes.csv');
    this.selection.clear();
  }

  exportAll() {
    const csv = this.convertToCSV(this.dataSource.data);
    this.downloadCSV(csv, 'tous-les-envois.csv');
  }

  private convertToCSV(data: Shipment[]): string {
    const headers = ['Tracking', 'Destinataire', 'Ville', 'Statut', 'Type', 'Coût', 'Poids', 'Date'];
    const rows = data.map(s => [
      s.trackingID,
      s.receiver_name,
      s.city,
      s.status,
      s.type,
      s.cost,
      s.weight,
      s.create_date
    ]);

    return [headers, ...rows].map(row => row.join(',')).join('\n');
  }

  private downloadCSV(csv: string, filename: string) {
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  // === HELPERS ===

  getStatusLabel(status: string): string {
    const option = this.statusOptions.find(opt => opt.value === status);
    if (option) {
      return this.translate.instant(option.labelKey);
    }

    return status
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  getTypeLabel(type: string): string {
    const option = this.typeOptions.find(opt => opt.value === type);
    if (option) {
      return this.translate.instant(option.labelKey);
    }

    return type.charAt(0).toUpperCase() + type.slice(1).replace('_', ' ');
  }

  refreshData() {
    this.fetchAllShipments();
  }

  // === STATISTICS ===

  get activeShipments(): Shipment[] {
    if (!this.dataSource?.data) return [];
    return this.dataSource.data.filter(s => !s.archived);
  }

  get shipmentsInTransit(): number {
    return this.activeShipments.filter(s =>
      s.status === 'collected_europe' ||
      s.status === 'warehouse' ||
      s.status === 'befret_transit'
    ).length;
  }

  get shipmentsArrivedCongo(): number {
    return this.activeShipments.filter(s => s.status === 'congo_distribution').length;
  }

  get shipmentsDelivered(): number {
    return this.activeShipments.filter(s => s.status === 'delivered').length;
  }

  get shipmentsPending(): number {
    return this.activeShipments.filter(s => s.status === 'order').length;
  }

  get shipmentsIncident(): number {
    const incidentStatuses = ['lost', 'damaged', 'poorly_packed', 'dangerous_goods', 'seized_customs_eu', 'seized_customs_congo'];
    return this.activeShipments.filter(s => incidentStatuses.includes(s.status)).length;
  }

  get shipmentsArchived(): number {
    if (!this.dataSource?.data) return 0;
    return this.dataSource.data.filter(s => s.archived).length;
  }

  /**
   * Filter by stat card click — sets filterStatus and triggers filtering
   */
  filterByStatCard(card: string): void {
    this.activeStatCard = card;

    if (card === 'all') {
      this.filterStatus = 'all';
    } else if (card === 'in_transit') {
      // Special multi-status: handled in filterPredicate
      this.filterStatus = 'in_transit';
    } else if (card === 'arrived_congo') {
      this.filterStatus = 'congo_distribution';
    } else if (card === 'delivered') {
      this.filterStatus = 'delivered';
    } else if (card === 'pending') {
      this.filterStatus = 'order';
    } else if (card === 'incident') {
      // Special multi-status: handled in filterPredicate
      this.filterStatus = 'incident';
    }

    this.onFilterChange();
  }

  /**
   * Convertit un Timestamp Firestore ou autre format en Date JavaScript
   * Résout l'erreur NG02100: InvalidPipeArgument pour le pipe date
   */
  private convertTimestampToDate(timestamp: any): Date {
    if (!timestamp) {
      return new Date();
    }

    // Si c'est un Timestamp Firestore (avec méthode toDate)
    if (timestamp && typeof timestamp.toDate === 'function') {
      return timestamp.toDate();
    }

    // Si c'est déjà une Date
    if (timestamp instanceof Date) {
      return timestamp;
    }

    // Si c'est un objet Firestore Timestamp sérialisé (seconds, nanoseconds)
    if (timestamp && typeof timestamp === 'object' && 'seconds' in timestamp) {
      return new Date(timestamp.seconds * 1000);
    }

    // Si c'est une string ISO ou un timestamp numérique
    if (typeof timestamp === 'string' || typeof timestamp === 'number') {
      return new Date(timestamp);
    }

    // Fallback
    return new Date();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get paginatedData(): Shipment[] {
    if (!this.dataSource?.filteredData?.length) return [];
    if (!this.paginator) return this.dataSource.filteredData.slice(0, 25);
    const start = this.paginator.pageIndex * this.paginator.pageSize;
    return this.dataSource.filteredData.slice(start, start + this.paginator.pageSize);
  }

  get currentPage(): number {
    return this.paginator ? this.paginator.pageIndex + 1 : 1;
  }

  get totalPages(): number {
    if (!this.paginator || !this.dataSource?.filteredData?.length) return 1;
    return Math.ceil(this.dataSource.filteredData.length / this.paginator.pageSize);
  }

  get hasPrevPage(): boolean {
    return this.paginator ? this.paginator.pageIndex > 0 : false;
  }

  get hasNextPage(): boolean {
    return this.paginator ? this.paginator.pageIndex < this.totalPages - 1 : false;
  }

  prevPage(): void {
    if (this.paginator && this.hasPrevPage) {
      this.paginator.previousPage();
    }
  }

  nextPage(): void {
    if (this.paginator && this.hasNextPage) {
      this.paginator.nextPage();
    }
  }
}
